import { z } from 'zod'

/**
 * Russian wording for zod's built-in validation errors.
 *
 * Set globally rather than per field. Nineteen numeric fields in the property
 * wizard alone carry a rule and no message, so each fell back to zod's English
 * default — «Number must be greater than 0» under «Высота потолков». Writing
 * the string at every call site would fix the ones we remember and leave the
 * next one in English.
 *
 * An explicit message on a schema still wins: this is consulted only for
 * issues that carry none, so the keyed messages the document wizard passes to
 * TranslatedFormMessage are untouched.
 *
 * Not routed through i18n. It is installed once at startup and the strings are
 * needed synchronously while parsing; the app is Russian-first with `lng: 'ru'`
 * fixed, so the indirection would buy nothing.
 */

/**
 * The app resolves zod 3, where an issue says what it is about in `type`.
 * `origin` is read as a fallback because the repository also has the v4 core
 * hoisted at the root, and a resolution change would otherwise silently drop
 * every message here back to English.
 */
type NumericIssue = {
	code: string
	origin?: string
	type?: string
	minimum?: number | bigint
	maximum?: number | bigint
	inclusive?: boolean
	expected?: string
	received?: string
}

const isNumeric = (issue: NumericIssue) => {
	const kind = issue.type ?? issue.origin
	return kind === 'number' || kind === 'int'
}

function translate(issue: NumericIssue): string | undefined {
	switch (issue.code) {
		case 'invalid_type':
			// An empty numeric input arrives as NaN, which reads as the wrong
			// type rather than as a missing value.
			if (issue.expected === 'number') return 'Введите число'
			// v3 spells it «integer», v4 «int».
			if (issue.expected === 'integer' || issue.expected === 'int')
				return 'Введите целое число'
			if (issue.received === 'undefined') return 'Заполните поле'
			return undefined

		case 'too_small': {
			if (!isNumeric(issue)) return undefined
			const min = issue.minimum
			if (!issue.inclusive) return `Должно быть больше ${min}`
			if (min === 0) return 'Не может быть отрицательным'
			return `Должно быть не меньше ${min}`
		}

		case 'too_big': {
			if (!isNumeric(issue)) return undefined
			return issue.inclusive
				? `Должно быть не больше ${issue.maximum}`
				: `Должно быть меньше ${issue.maximum}`
		}

		case 'not_multiple_of':
			return 'Введите целое число'

		default:
			return undefined
	}
}

export function installZodErrorMap() {
	// Falling back to `ctx.defaultError` leaves zod's own message in place,
	// which keeps this map to the cases it actually has wording for.
	z.setErrorMap((issue, ctx) => ({
		message: translate(issue as NumericIssue) ?? ctx.defaultError
	}))
}
